/*!
 * component: binding for web components 🥊  tko.binding.component@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { virtualElements, makeArray, cloneNodes } from 'tko.utils';
import { unwrap, isObservable } from 'tko.observable';
import { DescendantBindingHandler, applyBindingsToDescendants, contextFor } from 'tko.bind';
import { jsxToNode, maybeJsx, cloneNodeFromOriginal } from 'tko.utils.jsx';
import { NativeProvider } from 'tko.provider.native';
import { LifeCycle } from 'tko.lifecycle';
import registry from 'tko.utils.component';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

//
var componentLoadingOperationUniqueId = 0;
var ComponentBinding = /** @class */ (function (_super) {
    __extends(ComponentBinding, _super);
    function ComponentBinding(params) {
        var _this = _super.call(this, params) || this;
        _this.originalChildNodes = makeArray(virtualElements.childNodes(_this.$element));
        _this.computed('computeApplyComponent');
        return _this;
    }
    ComponentBinding.prototype.setDomNodesFromJsx = function (jsx, element) {
        var jsxArray = Array.isArray(jsx) ? jsx : [jsx];
        var domNodeChildren = jsxArray.map(jsxToNode);
        virtualElements.setDomNodeChildren(element, domNodeChildren);
    };
    ComponentBinding.prototype.cloneTemplateIntoElement = function (componentName, template, element) {
        var _this = this;
        if (!template) {
            throw new Error('Component \'' + componentName + '\' has no template');
        }
        if (maybeJsx(template)) {
            if (isObservable(template)) {
                this.subscribe(template, function (jsx) {
                    _this.setDomNodesFromJsx(jsx, element);
                    applyBindingsToDescendants(_this.childBindingContext, _this.$element);
                });
            }
            this.setDomNodesFromJsx(unwrap(template), element);
        }
        else {
            var clonedNodesArray = cloneNodes(template);
            virtualElements.setDomNodeChildren(element, clonedNodesArray);
        }
    };
    ComponentBinding.prototype.createViewModel = function (componentDefinition, element, originalChildNodes, componentParams) {
        var componentViewModelFactory = componentDefinition.createViewModel;
        return componentViewModelFactory
            ? componentViewModelFactory.call(componentDefinition, componentParams, { element: element, templateNodes: originalChildNodes })
            : componentParams; // Template-only component
    };
    /**
     * Return the $componentTemplateSlotNodes for the given template
     * @param {HTMLElement|jsx} template
     */
    ComponentBinding.prototype.makeTemplateSlotNodes = function (originalChildNodes) {
        return Object.assign.apply(Object, __spread([{}], this.genSlotsByName(originalChildNodes)));
    };
    /**
     * Iterate over the templateNodes, yielding each '<element slot=name>'
     * as an object * of {name: element}.
     * @param {HTMLElement} templateNodes
     */
    ComponentBinding.prototype.genSlotsByName = function (templateNodes) {
        var e_1, _a, _b, templateNodes_1, templateNodes_1_1, node, slotName, e_1_1;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 5, 6, 7]);
                    templateNodes_1 = __values(templateNodes), templateNodes_1_1 = templateNodes_1.next();
                    _c.label = 1;
                case 1:
                    if (!!templateNodes_1_1.done) return [3 /*break*/, 4];
                    node = templateNodes_1_1.value;
                    if (node.nodeType !== 1) {
                        return [3 /*break*/, 3];
                    }
                    slotName = node.getAttribute('slot');
                    if (!slotName) {
                        return [3 /*break*/, 3];
                    }
                    return [4 /*yield*/, (_b = {}, _b[slotName] = node, _b)];
                case 2:
                    _c.sent();
                    _c.label = 3;
                case 3:
                    templateNodes_1_1 = templateNodes_1.next();
                    return [3 /*break*/, 1];
                case 4: return [3 /*break*/, 7];
                case 5:
                    e_1_1 = _c.sent();
                    e_1 = { error: e_1_1 };
                    return [3 /*break*/, 7];
                case 6:
                    try {
                        if (templateNodes_1_1 && !templateNodes_1_1.done && (_a = templateNodes_1["return"])) _a.call(templateNodes_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                    return [7 /*endfinally*/];
                case 7: return [2 /*return*/];
            }
        });
    };
    ComponentBinding.prototype.computeApplyComponent = function () {
        var _this = this;
        var value = unwrap(this.value);
        var componentName;
        var componentParams;
        if (typeof value === 'string') {
            componentName = value;
        }
        else {
            componentName = unwrap(value.name);
            componentParams = NativeProvider.getNodeValues(this.$element) ||
                unwrap(value.params);
        }
        this.latestComponentName = componentName;
        if (!componentName) {
            throw new Error('No component name specified');
        }
        this.loadingOperationId = this.currentLoadingOperationId = ++componentLoadingOperationUniqueId;
        registry.get(componentName, function (defn) { return _this.applyComponentDefinition(componentName, componentParams, defn); });
    };
    ComponentBinding.prototype.applyComponentDefinition = function (componentName, componentParams, componentDefinition) {
        var _this = this;
        // If this is not the current load operation for this element, ignore it.
        if (this.currentLoadingOperationId !== this.loadingOperationId ||
            this.latestComponentName !== componentName) {
            return;
        }
        // Clean up previous state
        this.cleanUpState();
        var element = this.$element;
        // Instantiate and bind new component. Implicitly this cleans any old DOM nodes.
        if (!componentDefinition) {
            throw new Error('Unknown component \'' + componentName + '\'');
        }
        if (componentDefinition.template) {
            this.cloneTemplateIntoElement(componentName, componentDefinition.template, element);
        }
        var componentViewModel = this.createViewModel(componentDefinition, element, this.originalChildNodes, componentParams);
        var viewTemplate = componentViewModel && componentViewModel.template;
        if (!viewTemplate && !componentDefinition.template) {
            throw new Error('Component \'' + componentName + '\' has no template');
        }
        if (!componentDefinition.template) {
            this.cloneTemplateIntoElement(componentName, viewTemplate, element);
        }
        if (componentViewModel instanceof LifeCycle) {
            componentViewModel.anchorTo(this.$element);
        }
        var ctxExtender = function (ctx) { return Object.assign(ctx, {
            $component: componentViewModel,
            $componentTemplateNodes: _this.originalChildNodes,
            $componentTemplateSlotNodes: _this.makeTemplateSlotNodes(_this.originalChildNodes)
        }); };
        this.childBindingContext = this.$context.createChildContext(componentViewModel, /* dataItemAlias */ undefined, ctxExtender);
        this.currentViewModel = componentViewModel;
        var onBinding = this.onBindingComplete.bind(this, componentViewModel);
        this.applyBindingsToDescendants(this.childBindingContext, onBinding);
    };
    ComponentBinding.prototype.onBindingComplete = function (componentViewModel, bindingResult) {
        if (componentViewModel && componentViewModel.koDescendantsComplete) {
            componentViewModel.koDescendantsComplete(this.$element);
        }
        this.completeBinding(bindingResult);
    };
    ComponentBinding.prototype.cleanUpState = function () {
        var currentView = this.currentViewModel;
        var currentViewDispose = currentView && currentView.dispose;
        if (typeof currentViewDispose === 'function') {
            currentViewDispose.call(currentView);
        }
        this.currentViewModel = null;
        // Any in-flight loading operation is no longer relevant, so make sure we ignore its completion
        this.currentLoadingOperationId = null;
    };
    ComponentBinding.prototype.dispose = function () {
        this.cleanUpState();
        _super.prototype.dispose.call(this);
    };
    Object.defineProperty(ComponentBinding.prototype, "controlsDescendants", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComponentBinding, "allowVirtualElements", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return ComponentBinding;
}(DescendantBindingHandler));

/**
 * SlotBinding replaces a slot with
 */
var SlotBinding = /** @class */ (function (_super) {
    __extends(SlotBinding, _super);
    function SlotBinding() {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        var _this = _super.apply(this, __spread(params)) || this;
        var slotNode = _this.getSlot(_this.value);
        var $slotContext = contextFor(slotNode);
        var childContext = _this.$context.extend({
            $slotContext: $slotContext,
            $slotData: $slotContext && $slotContext.$data
        });
        _this.replaceSlotWithNode(_this.$element, slotNode);
        _this.applyBindingsToDescendants(childContext);
        return _this;
    }
    /**
     *
     * @param {HTMLElement} nodeToReplace
     * @param {HTMLElement}} slotValue
     */
    SlotBinding.prototype.replaceSlotWithNode = function (nodeInComponentTemplate, slotNode) {
        var nodesForSlot = cloneNodeFromOriginal(slotNode);
        virtualElements.setDomNodeChildren(nodeInComponentTemplate, nodesForSlot);
    };
    SlotBinding.prototype.getSlot = function (slotName) {
        var $componentTemplateSlotNodes = this.$context.$componentTemplateSlotNodes;
        if (!slotName) {
            return $componentTemplateSlotNodes[''] ||
                __spread(this.$context.$componentTemplateNodes).filter(function (n) { return !n.getAttribute || !n.getAttribute('slot'); });
        }
        return $componentTemplateSlotNodes[slotName];
    };
    Object.defineProperty(SlotBinding, "allowVirtualElements", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return SlotBinding;
}(DescendantBindingHandler));

var bindings = { component: ComponentBinding, slot: SlotBinding };

export { bindings };
//# sourceMappingURL=tko.binding.component.js.map
